/** @module Numbers */

/**
 * Ensures that the returned value is a number.
 * In case the given value is not a valid number, the fallback value is returned instead
 *
 * @param {number|string} value the number to ensure
 * @param {number} [fallbackValue=0] an optional fallback value (default 0)
 *
 * @return {number} the given value or the fallback value, never null, never undefined
 */
export function ensureNumberValue(value, fallbackValue = 0) {
    const number = Number.parseFloat(value);
    if (Number.isFinite(number)) return number;

    return ensureNumberValue(fallbackValue);
}

/**
 * calculate the average of an array of numbers
 * any non-numeric values in the array will be ignored
 *
 * @param {number[]} arr
 * @returns {number}
 */
export function averageFromArray(arr) {
    return arr
        .filter(item => !isNaN(item))
        .reduce((acc, curr, i) =>
            acc + (curr - acc) / (i + 1),
        0);
}

/**
 * Finds the quantile in the given array of numbers
 *
 * @param {number[]} arr the array to get the quantil from
 * @param {number} [q=.5] the desired quantile
 *
 * @returns {number} the quantile of the array
 */
export function quantil(arr, q = 0.5) {
    const arrayCopy = [...arr];
    arrayCopy.sort((a, b) => a - b);

    const pos = (arrayCopy.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (arrayCopy[base + 1] !== undefined) {
        return arrayCopy[base] + rest * (arrayCopy[base + 1] - arrayCopy[base]);
    } else {
        return arrayCopy[base];
    }
}

/**
 * Gets the median number in the given array
 *
 * @param {number[]} arr the array to get the median for
 *
 * @returns {number} the median value in the array
 */
export function median(arr) {
    return quantil(arr, .5);
}

/**
 * Format bytes as human-readable text.
 *
 * @param {number} bytes Number of bytes.
 * @param {boolean} [si=false] True to use metric (SI) units, aka powers of 1000. False to use binary (IEC), aka powers of 1024.
 * @param {number} [dp=1] Number of decimal places to display.
 *
 * @return {string} Formatted string.
 */
export function humanFileSize(bytes, si = false, dp = 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return `${bytes} B`;
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return `${bytes.toFixed(dp)} ${units[u]}`;
}

const unitPower = {
    K: 1,
    M: 2,
    G: 3,
    T: 4,
    P: 5,
    E: 6,
    Y: 7,
};
/**
 * Parses a human-readable file size string to bytes
 *
 * @param {string} sizeStr - Size string like '5MB', '100KB', etc.
 * @returns {number} Size in bytes
*/
export function parseFileSize(sizeStr) {
    if (typeof sizeStr !== 'string') {
        return 0;
    }

    const splitSize = sizeStr.split(/^(\d+(?:\.\d+)?)/i);

    const num = parseFloat(splitSize[1]);
    const unit = splitSize[2].toUpperCase();

    // 5MIB -> ['5', 'I', 'B']
    const splitUnit = unit.split('');

    const unitPrefix = splitUnit[0];
    const thresh = splitUnit[1] === 'B' ? 1000 : 1024;

    return num * Math.pow(thresh, unitPower[unitPrefix] ?? 0);
}

/**
 * Given total and partial this method returns the rounded
 * percentage
 *
 * @param {number} total the full quantity
 * @param {number} partial the current quantity
 *
 * @returns {string} the calculated percentage
 */
export function percentage(total, partial) {
    if (!total || !partial) { return '0%'; }
    return `${Math.round((partial / total) * 100)}%`;
}

/**
 * Given two numbers, this method checks if the first member falls within the
 * range of tolerance of the second member
 *
 * @param {number} a the first member of the check
 * @param {*} b the second member of the check
 * @param {*} [tolerance=10] an optional tolerance for the range check
 *
 * @returns {boolean} true if `a` falls within `tollerance` range from `b`
 */
export function inRange(a, b, tolerance = 10) {
    return Math.abs(a - b) <= tolerance;
}

/**
 * @typedef {'in'|'cm'} ConvertableUnits
 */

const INCH_TO_MM = 25.4;

/**
 * @param {number} size
 * @param {number} dpi
 * @param {ConvertableUnits} unit
 *
 * @returns {number} pixels
 */
export function unitToPixels(size, dpi, unit) {
    if (!size || !dpi) {
        console.warn('[numbers/unitToPixels] Received empty size or dpi values.');

        return size;
    }

    if (unit === 'in') {
        return Math.ceil(size * dpi);
    }

    if (unit === 'mm') {
        return size / INCH_TO_MM * dpi;
    }

    throw new Error(`[numbers/unitToPixels] Received unsupported unit: "${unit}"`);
}

/**
 * @param {number} size
 * @param {number} dpi
 * @param {ConvertableUnits} unit
 *
 * @returns {number} in passed unit
 */
export function pixelsToUnit(pixels, dpi, unit) {
    if (!pixels || !dpi) {
        console.warn('[numbers/pixelsToUnit] Received empty pixels or dpi values.');

        // setting toFixed cuts off fabric values, which often are passed as quite long floats.
        return Number.isFinite(pixels) ? pixels.toFixed(0) : pixels;
    }

    const sizeInInches = pixels / dpi;

    if (unit === 'in') {
        return sizeInInches.toFixed(2);
    }

    if (unit === 'mm') {
        return (sizeInInches * INCH_TO_MM).toFixed(2);
    }

    throw new Error(`[numbers/pixelsToUnit] Received unsupported unit: "${unit}"`);
}
